"use client";

import type { Variants } from "framer-motion";
import { motion, useAnimation, useReducedMotion } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface WalletHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface WalletProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const WalletIcon = forwardRef<WalletHandle, WalletProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const bodyVariants: Variants = {
      normal: { strokeDashoffset: 0, opacity: 1 },
      animate: {
        strokeDashoffset: [80, 0],
        opacity: [0.4, 1],
        transition: {
          duration: 0.8 * duration,
          ease: "easeInOut" as const,
        },
      },
    };

    const flapVariants: Variants = {
      normal: { rotate: 0, originX: 0.1, originY: 0.5 },
      animate: {
        rotate: [-6, 0, -3, 0],
        transition: {
          duration: 0.9 * duration,
          ease: "easeInOut" as const,
          delay: 0.2,
        },
      },
    };

    const swipeVariants: Variants = {
      normal: { x: 0, opacity: 0 },
      animate: {
        x: [0, 6, 0],
        opacity: [0, 1, 0],
        transition: {
          duration: 0.8 * duration,
          ease: "easeInOut" as const,
          delay: 0.45,
        },
      },
    };

    return (
      <motion.div
        className={`inline-flex items-center justify-center ${className || ""}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.path
            d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"
            strokeDasharray="80"
            strokeDashoffset="80"
            variants={bodyVariants}
            initial="normal"
            animate={controls}
          />
          <motion.g variants={flapVariants} initial="normal" animate={controls}>
            <motion.path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
          </motion.g>
          <motion.line
            x1="14"
            y1="12"
            x2="18"
            y2="12"
            variants={swipeVariants}
            initial="normal"
            animate={controls}
          />
        </motion.svg>
      </motion.div>
    );
  },
);

WalletIcon.displayName = "WalletIcon";
export { WalletIcon };
